import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus, Users } from 'lucide-react';

import { ApiError, tenantApi } from '@/features/auth/api';
import { useAuthStore } from '@/stores/auth-store';

// System-role fallback: tenants seeded before the role-catalog migration
// (and fresh signups whose onboarding hasn't finished seeding) come back
// with [] from /tenant/roles. Without this the Role <select> renders empty.
const SYSTEM_ROLE_FALLBACK = [
  { slug: 'super_admin', name: 'Super Admin' },
  { slug: 'ops_manager', name: 'Operations Manager' },
  { slug: 'hr_manager', name: 'HR Manager' },
  { slug: 'field_user', name: 'Field User' },
];

type Member = {
  user_id: string;
  email: string;
  full_name: string;
  role: string;
  custom_role_id: string | null;
  is_active: boolean;
  joined_at: string;
  last_accessed_at: string | null;
};

export function UsersPage() {
  const queryClient = useQueryClient();
  const perms = useAuthStore.getState().tenant?.permissions ?? [];
  const can = (perm: string) => perms.includes(perm);
  const currentUserId = useAuthStore.getState().user?.userId;

  const invites = useQuery({ queryKey: ['tenant', 'invites'], queryFn: tenantApi.invites });
  const roles = useQuery({ queryKey: ['tenant', 'roles'], queryFn: tenantApi.roles });
  const members = useQuery({
    queryKey: ['tenant', 'members'],
    queryFn: tenantApi.members,
  });

  const [showInvite, setShowInvite] = useState(false);

  const revoke = useMutation({
    mutationFn: (id: string) => tenantApi.revokeInvite(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tenant', 'invites'] }),
  });

  const rolesForSelect =
    (roles.data ?? []).length > 0
      ? roles.data!.map((r) => ({ slug: r.slug, name: r.name }))
      : SYSTEM_ROLE_FALLBACK;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <header className="page-header">
        <div>
          <h1 className="page-title flex items-center gap-2.5">
            <Users className="h-6 w-6 text-primary" />
            Team members
          </h1>
          <p className="page-subtitle">
            Active members, pending invitations, and deactivated accounts.
          </p>
        </div>
        {can('user.invite') && (
          <button onClick={() => setShowInvite(true)} className="btn-primary">
            <UserPlus className="h-4 w-4" />
            Invite users
          </button>
        )}
      </header>

      <section className="card !p-0">
        <header className="border-b border-border px-4 py-3 text-sm font-medium">
          Members ({(members.data ?? []).length})
        </header>
        {members.isLoading ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">Loading…</p>
        ) : members.error ? (
          <p className="px-4 py-6 text-sm text-danger">
            Failed to load members:{' '}
            {members.error instanceof Error ? members.error.message : 'unknown error'}
          </p>
        ) : (members.data ?? []).length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">No members yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(members.data ?? []).map((m) => (
                <MemberRow
                  key={m.user_id}
                  member={m}
                  roles={rolesForSelect}
                  canEditRole={can('user.update')}
                  canDeactivate={can('user.deactivate')}
                  canReactivate={can('user.update')}
                  isSelf={m.user_id === currentUserId}
                />
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="card !p-0">
        <header className="border-b border-border px-4 py-3 text-sm font-medium">
          Pending invitations
        </header>
        {invites.isLoading ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">Loading…</p>
        ) : (invites.data ?? []).length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground">No pending invitations.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Expires</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {invites.data!.map((i) => (
                <tr key={i.id} className="border-t border-border">
                  <td className="px-4 py-3">{i.email}</td>
                  <td className="px-4 py-3 text-muted-foreground">{i.role}</td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {new Date(i.expires_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => revoke.mutate(i.id)}
                      className="text-sm text-danger hover:underline"
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {showInvite && (
        <InviteUserModal
          roles={rolesForSelect}
          onClose={() => setShowInvite(false)}
          onSubmitted={() => {
            setShowInvite(false);
            queryClient.invalidateQueries({ queryKey: ['tenant', 'invites'] });
          }}
        />
      )}
    </div>
  );
}

function MemberRow({
  member,
  roles,
  canEditRole,
  canDeactivate,
  canReactivate,
  isSelf,
}: {
  member: Member;
  roles: Array<{ slug: string; name: string }>;
  canEditRole: boolean;
  canDeactivate: boolean;
  canReactivate: boolean;
  isSelf: boolean;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draftRole, setDraftRole] = useState(member.role);
  const [error, setError] = useState<string | null>(null);
  const [confirmingDeactivate, setConfirmingDeactivate] = useState(false);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['tenant', 'members'] });

  const saveRole = useMutation({
    mutationFn: () => tenantApi.updateMemberRole(member.user_id, draftRole),
    onSuccess: () => {
      setEditing(false);
      invalidate();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Update failed'),
  });

  const deactivate = useMutation({
    mutationFn: () => tenantApi.deactivateMember(member.user_id),
    onSuccess: () => {
      setConfirmingDeactivate(false);
      invalidate();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Deactivate failed'),
  });

  const reactivate = useMutation({
    mutationFn: () => tenantApi.reactivateMember(member.user_id),
    onSuccess: invalidate,
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Reactivate failed'),
  });

  return (
    <tr className="border-t border-border">
      <td className="px-4 py-3">
        {member.full_name}
        {isSelf && (
          <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
            you
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-muted-foreground">{member.email}</td>
      <td className="px-4 py-3">
        {editing ? (
          <div className="flex items-center gap-2">
            <select
              className="input"
              value={draftRole}
              onChange={(e) => setDraftRole(e.target.value)}
            >
              {roles.map((r) => (
                <option key={r.slug} value={r.slug}>
                  {r.name}
                </option>
              ))}
            </select>
            <button
              onClick={() => saveRole.mutate()}
              disabled={saveRole.isPending || draftRole === member.role}
              className="text-sm text-primary hover:underline disabled:opacity-50"
            >
              {saveRole.isPending ? 'Saving…' : 'Save'}
            </button>
            <button
              onClick={() => {
                setEditing(false);
                setDraftRole(member.role);
                setError(null);
              }}
              className="text-sm text-muted-foreground hover:underline"
            >
              Cancel
            </button>
          </div>
        ) : (
          <span className="text-muted-foreground">{member.role}</span>
        )}
      </td>
      <td className="px-4 py-3">
        {member.is_active ? (
          <span className="rounded-full bg-success/10 px-2 py-0.5 text-xs text-success">
            Active
          </span>
        ) : (
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
            Deactivated
          </span>
        )}
        {error && (
          <p className="mt-1 text-xs text-danger" role="alert">
            {error}
          </p>
        )}
      </td>
      <td className="px-4 py-3 text-right">
        <div className="flex justify-end gap-3 text-sm">
          {canEditRole && member.is_active && !editing && (
            <button
              onClick={() => {
                setEditing(true);
                setError(null);
              }}
              className="text-primary hover:underline"
            >
              Change role
            </button>
          )}
          {!member.is_active && canReactivate && (
            <button
              onClick={() => reactivate.mutate()}
              disabled={reactivate.isPending}
              className="text-primary hover:underline"
            >
              {reactivate.isPending ? 'Reactivating…' : 'Reactivate'}
            </button>
          )}
          {member.is_active && canDeactivate && !isSelf && !confirmingDeactivate && (
            <button
              onClick={() => setConfirmingDeactivate(true)}
              className="text-danger hover:underline"
            >
              Deactivate
            </button>
          )}
          {confirmingDeactivate && (
            <span className="flex items-center gap-2">
              <span className="text-muted-foreground">Confirm?</span>
              <button
                onClick={() => deactivate.mutate()}
                disabled={deactivate.isPending}
                className="text-danger hover:underline"
              >
                {deactivate.isPending ? 'Deactivating…' : 'Yes'}
              </button>
              <button
                onClick={() => setConfirmingDeactivate(false)}
                className="text-muted-foreground hover:underline"
              >
                No
              </button>
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}

function InviteUserModal({
  roles,
  onClose,
  onSubmitted,
}: {
  roles: Array<{ slug: string; name: string }>;
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const [emails, setEmails] = useState('');
  const [role, setRole] = useState(roles[0]?.slug ?? 'ops_manager');
  const [message, setMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const list = emails
        .split(/[,\s]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (list.length === 0) throw new Error('Enter at least one email.');
      await tenantApi.createInvites({
        emails: list,
        role,
        personal_message: message || undefined,
      });
      onSubmitted();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invitation failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-30 grid place-items-center bg-black/50 px-4">
      <div className="card w-full max-w-md">
        <h2 className="text-lg font-semibold">Invite team members</h2>
        <form className="mt-4 space-y-4" onSubmit={submit}>
          <div>
            <label className="block text-sm font-medium">
              Emails <span className="text-muted-foreground">(comma- or newline-separated)</span>
            </label>
            <textarea
              required
              rows={3}
              className="input mt-1"
              value={emails}
              onChange={(e) => setEmails(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Role</label>
            <select value={role} onChange={(e) => setRole(e.target.value)} className="input mt-1">
              {roles.map((r) => (
                <option key={r.slug} value={r.slug}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium">Personal message (optional)</label>
            <textarea
              rows={2}
              className="input mt-1"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>
          {error && (
            <p className="rounded bg-danger/10 px-3 py-2 text-xs text-danger" role="alert">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={submitting} className="btn-primary">
              {submitting ? 'Sending…' : 'Send invites'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
